import type { CommunitySponsorSummary, CommunityClassroomSummary, CommunityLockerRoomSummary } from '@/types/community';

export interface ApiError {
  status: number;
  body: unknown;
}

interface RequestOptions {
  signal?: AbortSignal;
}

interface CommunitySponsorsOptions extends RequestOptions {
  limit?: number;
}

interface CheckoutOptions extends RequestOptions {
  token?: string | null;
  successPath?: string;
  cancelPath?: string;
}

function isJsonResponse(response: Response): boolean {
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('application/json');
}

async function readBody(response: Response): Promise<unknown> {
  if (isJsonResponse(response)) {
    try {
      return await response.json();
    } catch (_err) {
      return null;
    }
  }

  try {
    return await response.text();
  } catch (_err) {
    return '';
  }
}

async function parseResponse(response: Response): Promise<unknown> {
  const body = await readBody(response);
  if (!response.ok) {
    throw { status: response.status, body } satisfies ApiError;
  }
  return body;
}

export function isApiError(error: unknown): error is ApiError {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as Partial<ApiError>;
  return typeof candidate.status === 'number';
}

function toSponsorArray(items: unknown): CommunitySponsorSummary[] {
  if (!Array.isArray(items)) return [];
  return items.filter((item): item is CommunitySponsorSummary => typeof item === 'object' && item !== null) as CommunitySponsorSummary[];
}

function toClassroomArray(items: unknown): CommunityClassroomSummary[] {
  if (!Array.isArray(items)) return [];
  return items.filter((item): item is CommunityClassroomSummary => typeof item === 'object' && item !== null) as CommunityClassroomSummary[];
}

function toLockerRoomArray(items: unknown): CommunityLockerRoomSummary[] {
  if (!Array.isArray(items)) return [];
  return items.filter((item): item is CommunityLockerRoomSummary => typeof item === 'object' && item !== null) as CommunityLockerRoomSummary[];
}

function toRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

function toSearchParams(params: Record<string, string>): string {
  const search = new URLSearchParams(params);
  return search.toString();
}

export async function getCommunitySponsors(
  query: string,
  options: CommunitySponsorsOptions = {},
): Promise<CommunitySponsorSummary[]> {
  const limit = options.limit ?? 12;
  const params: Record<string, string> = { limit: String(limit) };
  if (query) {
    params.q = query;
  }

  const url = `${CORE_API}/api/v1/community/sponsors?${toSearchParams(params)}`;
  const response = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: options.signal,
  });
  const body = toRecord(await parseResponse(response));
  const payload = toRecord(body.data ?? body);
  return toSponsorArray(payload.items);
}

export async function getCommunityHighlights(options: CommunitySponsorsOptions = {}): Promise<CommunitySponsorSummary[]> {
  const limit = options.limit ?? 6;
  const params: Record<string, string> = { limit: String(limit) };
  const url = `${CORE_API}/api/v1/community/sponsors/random?${toSearchParams(params)}`;
  const response = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: options.signal,
  });
  const body = toRecord(await parseResponse(response));
  const payload = body.data ?? body;
  return toSponsorArray(Array.isArray(payload) ? payload : (payload as Record<string, unknown>).items);
}

async function postSponsorCheckout(payload: Record<string, unknown>, options: CheckoutOptions = {}): Promise<Record<string, unknown>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }

  const response = await fetch(`${CORE_API}/api/v1/sponsors/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal: options.signal,
  });

  const body = await parseResponse(response);
  return toRecord(body);
}

export async function startProgramCheckout(
  amountCents: number,
  options: CheckoutOptions = {},
): Promise<Record<string, unknown>> {
  const payload: Record<string, unknown> = {
    mode: 'fund_program',
    amount_cents: amountCents,
  };

  if (options.successPath) {
    payload.success_path = options.successPath;
  }
  if (options.cancelPath) {
    payload.cancel_path = options.cancelPath;
  }

  return postSponsorCheckout(payload, options);
}

export async function startPlatformCheckout(
  amountCents: number,
  options: CheckoutOptions = {},
): Promise<Record<string, unknown>> {
  const payload: Record<string, unknown> = {
    mode: 'fund_platform',
    amount_cents: amountCents,
  };

  if (options.successPath) {
    payload.success_path = options.successPath;
  }
  if (options.cancelPath) {
    payload.cancel_path = options.cancelPath;
  }

  return postSponsorCheckout(payload, options);
}

export async function getCommunityClassrooms(options: CommunitySponsorsOptions = {}): Promise<CommunityClassroomSummary[]> {
  const limit = options.limit ?? 6;
  const params: Record<string, string> = { limit: String(limit) };
  const url = `${CORE_API}/api/v1/community/classrooms?${toSearchParams(params)}`;
  const response = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: options.signal,
  });
  const body = toRecord(await parseResponse(response));
  const payload = toRecord(body.data ?? body);
  return toClassroomArray(payload.items);
}

export async function getCommunityLockerRooms(options: CommunitySponsorsOptions = {}): Promise<CommunityLockerRoomSummary[]> {
  const limit = options.limit ?? 6;
  const params: Record<string, string> = { limit: String(limit) };
  const url = `${CORE_API}/api/v1/community/locker-rooms?${toSearchParams(params)}`;
  const response = await fetch(url, {
    headers: { accept: 'application/json' },
    signal: options.signal,
  });
  const body = toRecord(await parseResponse(response));
  const payload = toRecord(body.data ?? body);
  return toLockerRoomArray(payload.items);
}
